/**
 * Content for the public sample report.
 *
 * DATA ONLY — no JSX, no styling decisions. Split from SampleReportPage.jsx to
 * keep both files under the 500-line limit in CLAUDE.md.
 *
 * ── WHERE EVERY VALUE COMES FROM ────────────────────────────────────────────
 *
 * All of it is ONE REAL AUDIT: backend/tests/fixtures/roarsinc_v2_report.json,
 * `audit_1512ecaff8a6`, a three-page paid run against roarsinc.com started
 * 2026-08-31T13:26:56Z and completed 13:33:28Z. Nothing here is written for the
 * page. Every score, every count and every finding is transcribed from that
 * record.
 *
 * `backend/tests/test_sample_report_matches_fixture.py` parses this literal and
 * compares it to the fixture field by field. If the audit is re-run or the
 * fixture replaced, the build fails rather than serving last month's numbers as
 * this month's. Same shape as test_homepage_report_card_matches_fixture.
 *
 * ── DERIVATION ──────────────────────────────────────────────────────────────
 *
 * The record's own `score_derivation`, reproduced rather than restated:
 *
 *     Dimension scores (after severity caps): 52, 62, 55, 38, 50, 40
 *     Mean of 6 assessed dimensions:          49.5
 *     Overall:                                50
 *
 * n=6, base 49.5, cap 74, not capped, display 50. The overall score therefore
 * derives from this record's own dimension scores — which the record it
 * replaced did not, and which was one of the three reasons that record could
 * not be published.
 *
 * Validation: 11 findings received, 8 kept, 3 dropped. The 8 kept are below,
 * in the fixture array's order (3, 1, 2, 6, 7, 9, 10, 11 — NOT ascending, and
 * not sorted here, because the pin compares position by position). The ids are
 * the pipeline's own and are non-contiguous precisely because three were
 * dropped; the record this replaces had hand-written sequential ids.
 *
 * ── THE GAP: SIX DIMENSION SUMMARIES ARE NOT PUBLISHED ──────────────────────
 *
 * The record carries a prose summary per dimension. THREE OF THE SIX CITE
 * FINDINGS THAT THIS RECORD'S OWN VALIDATION BLOCK RECORDS AS DROPPED:
 *
 *     visual_hierarchy  cites the missing H1        dropped: hedged claim 'may'
 *     accessibility     cites the missing H1        dropped: hedged claim 'may'
 *                       and "unmarked form fields"  dropped: evidence quote not
 *                                                   found in captured HTML
 *     friction_points   cites form inputs lacking   dropped: same finding
 *                       visible labels
 *
 * `validation.prose_rebuilt` is ["scoreJustification", "executiveSummary",
 * "purposeFit"] — dimension summaries are NOT in it, so they were never
 * rebuilt after the drops. Publishing them would put prose on a public page
 * that contradicts the findings list directly beneath it.
 *
 * So the SCORES publish and the PARAGRAPHS do not. The six bars, the six
 * dimension names and the six real numbers are all here; only the prose is
 * withheld, and it comes back unchanged the moment the pipeline rebuilds
 * dimension summaries on a drop the way it already rebuilds the other three.
 * That defect is logged in docs/analysis/open-items.md and affects every
 * customer report, not this page.
 *
 * NOT hand-edited to remove the offending clauses. Editing a real record to
 * read better on a sales page is the exact move this file exists to refuse;
 * withholding a field and naming the reason is not the same act.
 *
 * ── REDACTED, DELIBERATELY ──────────────────────────────────────────────────
 *
 * The fixture has `user_id` REMOVED. It was the owner's real account id and it
 * is needed by no assertion. It is deleted rather than replaced with a
 * plausible synthetic value, so a reader sees an absence and this note rather
 * than a stand-in they might mistake for the record.
 *
 * `app_description` is in the record and is not transcribed; nothing renders it.
 *
 * ── WHY THE SUBJECT IS NAMED "Roars-august312026" ───────────────────────────
 *
 * Because that is the app_name on the record, and
 * test_the_subject_is_the_audited_url_and_not_a_stand_in asserts the page names
 * exactly what was audited. It reads like a test run because it was typed as
 * one. Weakening that assertion for cosmetics would remove the only thing
 * stopping this page showing one product's name over another's score, which is
 * a live known gap on the homepage card. A cleaner name belongs on the next
 * run, not in this file.
 *
 * ── WHY A 50 AND NOT A 72 ───────────────────────────────────────────────────
 *
 * 50 is a worse number than 72 and it is the one we can defend. A prospect
 * reading a real product scoring 50, with eight specific defects and the pixel
 * measurements behind them, learns what the product does. A prospect reading an
 * invented 72 learns what our marketing department does.
 *
 * NOTHING IS ROUNDED, SOFTENED OR IMPROVED.
 */

/** The subject. `input_data` and `app_name` from the fixture, verbatim. */
export const SUBJECT = {
  appName: 'Roars-august312026',
  url: 'https://www.roarsinc.com/',
  auditId: 'audit_1512ecaff8a6',
  completedAt: '2026-08-31',
};

/**
 * `overall_score`, and the BAND that score falls in.
 *
 * There is no letter to leave out any more: this record carries no `grade` and
 * no `score_label` at all, unlike the one it replaces. The band comes from
 * SCORE_BANDS, the one table every gauge, legend, badge and chip reads, and the
 * pin recomputes it with audit.scoring.band_label rather than trusting this
 * string.
 */
export const SCORE = { value: 50, band: 'Needs Work' };

/**
 * `score_justification`, verbatim.
 *
 * NOTE THE PATH: this record has no top-level `score_justification`. It lives
 * at `v2_report.score_justification`, which is a shape change from the record
 * this replaces and is reported in the handback rather than mapped silently.
 *
 * It is a count, and the count reconciles: the 8 findings below are 1 critical,
 * 5 major, 2 minor.
 */
export const SCORE_JUSTIFICATION =
  'Score reflects the 8 verified findings: 1 Critical, 5 Major, 2 Minor.';

/**
 * `executive_summary`, verbatim and complete.
 *
 * The record it replaces had one that ended mid-word, which is why the previous
 * transcription refused to publish it. That was a write-time truncation defect,
 * closed in 3b263df when max_tokens moved 8192 -> 16000; this summary ends on a
 * full sentence, checked rather than assumed.
 */
export const EXECUTIVE_SUMMARY =
  'The user experience audit for roarsinc.com reveals several significant '
  + 'issues, granting the site an overall score derived from both critical and '
  + 'major findings across various dimensions. The findings include one '
  + 'critical issue, five major issues, and a couple of minor ones impacting '
  + 'navigation, accessibility, and conversion rates. The most pressing '
  + 'concerns include duplicated text in navigation and headlines and a mobile '
  + 'popup obstructing essential content. Addressing these involves '
  + 'eliminating redundant DOM elements to improve semantic structure, '
  + 'altering contrast ratios for accessibility, and ensuring popups are not '
  + 'intrusive by appearing only after user interaction or a defined period. '
  + 'These changes aim to enhance user experience, readability, and engagement '
  + 'with the site.';

/**
 * The six dimension scores, keyed the way DimensionBars expects.
 *
 * The fixture stores them as nav_score / visual_score / friction_score /
 * a11y_score / conv_score / mobile_score; the mapping to these keys is
 * server._DIM_TO_FIELD's, mirrored in AuditReportPage's DIM_KEY_BY_CARD.
 * SIX, and Onboarding is not one of them.
 */
export const DIMENSION_SCORES = {
  navigation: 52,
  visual_hierarchy: 62,
  friction_points: 55,
  accessibility: 38,
  conversion_design: 50,
  mobile_ux: 40,
};

/**
 * The six dimensions and their REAL scores. No `text` field — see the gap note
 * in this file's header. The scores are the same values DIMENSION_SCORES
 * carries, repeated here because the cards and the bars are drawn from
 * different structures and the pin asserts they agree.
 */
export const DIMENSION_SUMMARIES = [
  {
    key: 'navigation',
    name: 'Navigation',
    score: 52,
  },
  {
    key: 'visual_hierarchy',
    name: 'Visual Hierarchy',
    score: 62,
  },
  {
    key: 'friction_points',
    name: 'Friction Points',
    score: 55,
  },
  {
    key: 'accessibility',
    name: 'Accessibility',
    score: 38,
  },
  {
    key: 'conversion_design',
    name: 'Conversion Design',
    score: 50,
  },
  {
    key: 'mobile_ux',
    name: 'Mobile UX',
    score: 40,
  },
];

/**
 * `findings`, verbatim, in fixture order — 3, 1, 2, 6, 7, 9, 10, 11.
 *
 * NOT sorted. The pin compares the id list position by position against the
 * fixture array, so sorting them here would be a silent reordering of the
 * record.
 *
 * THIS AUDIT HAS ONE CRITICAL FINDING, unlike the record it replaces, so the
 * critical row does render. SEVERITY_COUNTS is still built by filtering rather
 * than by listing three tiers: the rule is that a tier with no findings must
 * not draw a zero in the full critical treatment, and that rule has to hold for
 * whatever the NEXT audit contains, not just this one.
 *
 * `dimension` is the fixture's own value (mobileUX, conversion) and is NOT
 * normalised here; the display name is carried alongside it instead, so the
 * transcription stays a transcription.
 */
export const FINDINGS = [
  {
    id: 3,
    severity: 'major',
    dimension: 'accessibility',
    dimensionName: 'Accessibility',
    effort: 'low',
    impact: 'medium',
    title: "Five stat sublabels ('Years of Excellence', 'Happy Customers', 'Global Awards', 'Projects', 'AI-Driven Solutions') fail WCAG AA at 3.95:1 on #ffffff",
    userImpact: 'Users with reduced contrast sensitivity cannot reliably read the '
      + 'labels that contextualize the five social-proof numbers, undermining the '
      + "stat block's persuasive function.",
    recommendation: 'Change the sublabel text color from #808080 to #757575 or '
      + 'darker (e.g., #666666 gives 5.74:1 on #ffffff) to meet the 4.5:1 threshold.',
  },
  {
    id: 1,
    severity: 'critical',
    dimension: 'navigation',
    dimensionName: 'Navigation',
    effort: 'medium',
    impact: 'high',
    title: "All heading and navigation texts are duplicated verbatim in the DOM (e.g., 'Industries Industries', 'Restaurant Restaurant', 'Home Home')",
    userImpact: 'Screen readers announce each item twice. Search engines see '
      + 'duplicate anchor and heading text, degrading crawl quality. Visual users '
      + 'who inspect source or use browser find-in-page see doubled strings.',
    recommendation: 'Audit the template rendering the nav and headings. Each item '
      + 'is almost certainly being output once by a visible element and once by a '
      + 'hidden duplicate (e.g., an aria-label or a visually hidden span). Remove '
      + 'the duplicate text node, keeping only a single readable string per element.',
  },
  {
    id: 2,
    severity: 'major',
    dimension: 'mobileUX',
    dimensionName: 'Mobile UX',
    effort: 'low',
    impact: 'high',
    title: 'Popup overlay fires on page load covering the hero headline and entire stat bar on mobile',
    userImpact: 'A first-time visitor arriving on mobile cannot read the full '
      + 'headline or any of the social-proof stats without dismissing the popup '
      + 'first, adding friction at the most critical trust-building moment.',
    recommendation: 'Do not trigger the case-study popup on initial page load. '
      + "Trigger it on a deliberate user action (e.g., clicking a 'See our work' "
      + 'CTA) or after a meaningful dwell time (30+ seconds), and ensure it never '
      + 'covers the above-the-fold hero content.',
  },
  {
    id: 6,
    severity: 'major',
    dimension: 'mobileUX',
    dimensionName: 'Mobile UX',
    effort: 'low',
    impact: 'high',
    title: "15 mobile tap targets are below 44×44px minimum, including the logo (118×22px), 'Read More' CTA (116×39px), and nine service links (330×20px each)",
    userImpact: 'Mobile users tapping the logo or any service link risk mis-taps '
      + 'or failed activations, degrading navigation to services and the homepage.',
    recommendation: 'Add min-height: 44px and min-width: 44px to the logo anchor. '
      + 'Increase line-height or add padding-top/padding-bottom to the service list '
      + "items so each reaches 44px touch height. Increase the 'Read More' button "
      + 'height to 44px.',
  },
  {
    id: 7,
    severity: 'major',
    dimension: 'mobileUX',
    dimensionName: 'Mobile UX',
    effort: 'low',
    impact: 'high',
    title: 'All eight primary mobile nav items are 0px apart from their neighbors, creating a continuous mis-tap zone',
    userImpact: 'Mobile users attempting to tap a specific navigation item will '
      + 'frequently activate an adjacent one, causing unintended page navigation.',
    recommendation: 'Add at least 8px vertical padding or margin between each '
      + 'navigation list item in the mobile menu so adjacent tap targets have a '
      + 'minimum 8px gap.',
  },
  {
    id: 9,
    severity: 'major',
    dimension: 'conversion',
    dimensionName: 'Conversion Design',
    effort: 'low',
    impact: 'high',
    title: "No testimonial, review/rating, or case-study markers present in the HTML despite a 'What They Say' section visible on screen",
    userImpact: 'First-time visitors evaluating whether to engage Roars cannot '
      + "verify the 'What They Say' quotes through any linked external review "
      + "platform, and the page's Clutch awards are not linked to verifiable "
      + 'external pages.',
    recommendation: "Wrap the testimonial quote by 'Jayeis Sonill, CEO Aum "
      + "Investment' in a <blockquote> element. Link the Clutch badge images to "
      + 'the corresponding Clutch.co profile pages. Add markup or links to at '
      + 'least one external review platform (Clutch, GoodFirms) so the credibility '
      + 'signals are verifiable.',
  },
  {
    id: 10,
    severity: 'minor',
    dimension: 'accessibility',
    dimensionName: 'Accessibility',
    effort: 'low',
    impact: 'low',
    title: "Page title is duplicated verbatim: 'Product Development Company for Startups & SMEs | Roars Product Development Company for Startups & SMEs | Roars'",
    userImpact: 'Browser tabs, bookmark labels, and search-result titles all show '
      + 'the doubled string, which looks broken and reduces click-through credibility.',
    recommendation: "Set the <title> to a single instance: 'Product Development "
      + "Company for Startups & SMEs | Roars'.",
  },
  {
    id: 11,
    severity: 'minor',
    dimension: 'mobileUX',
    dimensionName: 'Mobile UX',
    effort: 'low',
    impact: 'low',
    title: "'Skip to content' link renders at 1×1px on mobile — effectively invisible and untappable",
    userImpact: 'Keyboard and switch-access users cannot use the skip link to '
      + 'bypass the navigation, forcing traversal of all nav items to reach main '
      + 'content.',
    recommendation: 'Style the skip link to be visibly positioned off-screen but '
      + 'focusable (position: absolute; left: -9999px) and reveal it on :focus with '
      + 'position: static; width: auto; height: auto. Ensure it is at least 44×44px '
      + 'when focused.',
  },
];

/**
 * Counts BY FILTER, never by a hand-written list of three tiers. A tier with no
 * findings does not appear — this audit happens to have all three, and the rule
 * exists for the audit that does not.
 */
export const SEVERITY_COUNTS = ['critical', 'major', 'minor']
  .map((tier) => ({ tier, count: FINDINGS.filter((f) => f.severity === tier).length }))
  .filter((row) => row.count > 0);


/**
 * THE SWITCH. Four things move together so they cannot be half-flipped:
 *
 *   1. the page's own noIndex          (SampleReportPage.jsx:73)
 *   2. its sitemap entry               (scripts/gen-sitemap.mjs:38)
 *   3. the links on / and /case-studies/ and /ux-auditor/
 *   4. the link on /compare/ and /pricing/
 *
 * IT IS NOW TRUE. The hold existed because the record behind the page was not a
 * real audit: hand-written sequential ids, an overall_score that derived from
 * nothing, and a scoreJustification citing a finding its own validation block
 * recorded as dropped. All three are answered by audit_1512ecaff8a6 — the ids
 * are the pipeline's and are non-contiguous, the score derives from the six
 * dimension scores (mean 49.5 -> 50), and the justification is a count that
 * reconciles with the kept findings.
 *
 * test_the_page_is_published_on_all_four_gates asserts they stay together in
 * this direction too. A half-flip is as possible after cutover as before it.
 */
export const SAMPLE_REPORT_PUBLISHED = true;

/**
 * One string for all surfaces, so they read as one thing rather than as five
 * links that happen to point at the same page.
 *
 * Names what it is instead of describing it, and carries no claim: no count, no
 * score, nothing that becomes false when the record is re-run.
 */
export const SAMPLE_REPORT_LINK_TEXT = 'See a real audit report';
